import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useHistoricalData } from '@/api/hooks/useHistoricalData';
import { DataPoint as ApiDataPoint } from '@/api/historical-data/types';
import dayjs from 'dayjs';
import { EventTimeline } from '@/components/Common/event-timeline';
import { useSitesWithLatestData } from '@/api/hooks/useSites';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    Area,
    AreaChart,
    BarChart,
    Bar,
    ComposedChart,
    ReferenceLine
} from 'recharts';

// Color theme constants
const THEME = {
    softStart: '#90CAF9',    // Light blue
    asp: '#FFB74D',         // Orange
    flowSave: '#80CBC4',    // Teal
    softClose: '#B0BEC5',   // Gray
    none: '#E0E0E0',       // Light gray

    // Chart colors
    consumption: {
        line: '#0E7EE4',     // Blue 50
        area: 'rgba(14, 126, 228, 0.1)'
    },
    solar: {
        actual: '#F2AA32',    // Orange 40
        expected: '#F26428'   // Orange (dashed)
    },
    temperature: {
        outdoor: '#109095',   // Teal
        setTemp: '#0E7EE4',   // Blue 50
        indoor: '#14B0BC'     // Mid Green
    },
    working: '#0A4E8B',     // Dark Blue
    occupancy: '#9CA3AF',   // Neutral 50
    ahu: {
        cooling: '#0E7EE4',   // Blue 50
        fan: '#8BC6FF'        // Light Blue
    }
};

// Define types for the chart data
interface ChartDataPoint {
    time: string;
    acPower?: number;
    totalPower?: number;
    gridImport?: number;
    solarGeneration?: number;
    expectedSolar?: number;
    outdoorTemp?: number;
    setTemp?: number;
    avgIndoorTemp?: number;
    totalWorking?: number;
    occupancy?: number;
    coolingMode?: number;
    fanMode?: number;
}

// Define type for chart data formatting
interface TooltipPayload {
    value: number;
    name: string;
    color: string;
}

// Helper function to get date range for API calls
const getDateRange = (selectedDate?: dayjs.Dayjs) => {
    // If a date is provided, use it; otherwise use the current date
    const date = selectedDate ? selectedDate.toDate() : new Date();

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return {
        start_timestamp: `${year}-${month}-${day}T00:00:00+07:00`,
        end_timestamp: `${year}-${month}-${day}T23:59:59+07:00`,
        tz: 'Asia/Bangkok'
    };
};

// Helper function to generate full day time points (00:00 to 23:59)
const generateFullDayTimePoints = (data: ChartDataPoint[]): ChartDataPoint[] => {
    // Create a map with all existing data points
    const dataMap = new Map<string, ChartDataPoint>();
    data.forEach(point => {
        dataMap.set(point.time, point);
    });

    // Generate all time points for a full day (every 5 minutes)
    const fullDayData: ChartDataPoint[] = [];
    for (let hour = 0; hour < 24; hour++) {
        for (let minute = 0; minute < 60; minute += 5) {
            const timeStr = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
            const existingPoint = dataMap.get(timeStr);

            if (existingPoint) {
                fullDayData.push(existingPoint);
            } else {
                fullDayData.push({ time: timeStr });
            }
        }
    }

    return fullDayData;
};

// Custom tooltip formatter for Recharts
const formatTooltip = (value: any, name: string, props: any) => {
    if (value !== undefined && value !== null) {
        const formattedValue = typeof value === 'number' ? value.toFixed(1) : value;
        return [formattedValue, name];
    }
    return [null, name];
};

// Custom label formatter for time display
const formatLabel = (label: string) => {
    return label;
};

interface DashboardProps {
    selectedDate?: dayjs.Dayjs;
    eventTypes?: any[];
    profileName?: string;
    eventLogData?: any[];
    eventLogLoading?: boolean;
}

const Dashboard: React.FC<DashboardProps> = ({ selectedDate, eventTypes, profileName, eventLogData, eventLogLoading }) => {
    // State for chart data
    const [energyChartData, setEnergyChartData] = useState<ChartDataPoint[]>([]);
    const [temperatureChartData, setTemperatureChartData] = useState<ChartDataPoint[]>([]);
    const [ahuChartData, setAhuChartData] = useState<ChartDataPoint[]>([]);

    // State for legend visibility
    const [energyLegendVisible, setEnergyLegendVisible] = useState({
        acPower: true,
        totalPower: true,
        gridImport: true,
        solarGeneration: true,
        expectedSolar: true,
        outdoorTemp: true,
        avgIndoorTemp: true
    });

    const [temperatureLegendVisible, setTemperatureLegendVisible] = useState({
        occupancy: true,
        setTemp: true,
        avgIndoorTemp: true
    });

    const [ahuLegendVisible, setAhuLegendVisible] = useState({
        coolingMode: true,
        fanMode: true,
        totalWorking: true
    });

    // Fetch sites data to get the site ID dynamically
    const { data: sitesData, isLoading: isSitesLoading } = useSitesWithLatestData();
    const siteId = useMemo(() => {
        // Use the first site's ID if available, otherwise fallback to a default
        return sitesData?.data?.[0]?.id || 'dohome-rama2';
    }, [sitesData]);

    // Get date range for API calls based on selected date
    const dateRange = useMemo(() => getDateRange(selectedDate), [selectedDate]);

    // Log date changes for debugging and reset chart data
    useEffect(() => {
        console.log('Dashboard selectedDate changed:', selectedDate?.format('YYYY-MM-DD'));
        console.log('Date range:', dateRange);

        // Reset chart data when date changes to ensure no stale data is shown
        setEnergyChartData([]);
        setTemperatureChartData([]);
        setAhuChartData([]);
    }, [selectedDate]);

    // Fetch data for Power Consumption & Generation chart
    const { data: acPowerData, isLoading: isAcPowerLoading } = useHistoricalData({
        device_id: 'ac_subsystem',
        datapoints: ['power'],
        resampling: '5min',
        start_timestamp: dateRange.start_timestamp,
        end_timestamp: dateRange.end_timestamp
    });

    const { data: solarData, isLoading: isSolarLoading } = useHistoricalData({
        device_id: 'solar_subsystem',
        datapoints: ['power', 'expected_power', 'grid_power'],
        resampling: '5min',
        start_timestamp: dateRange.start_timestamp,
        end_timestamp: dateRange.end_timestamp
    });

    const { data: outdoorTempData, isLoading: isOutdoorTempLoading } = useHistoricalData({
        device_id: siteId,
        datapoints: ['outdoor_temperature'],
        resampling: '5min',
        start_timestamp: dateRange.start_timestamp,
        end_timestamp: dateRange.end_timestamp
    });

    // Fetch data for Temperature Control & Occupancy chart
    const { data: zoneData, isLoading: isZoneLoading } = useHistoricalData({
        device_id: 'zone_main',
        datapoints: ['avg_temperature_setpoint', 'avg_room_temperature'],
        resampling: '5min',
        start_timestamp: dateRange.start_timestamp,
        end_timestamp: dateRange.end_timestamp
    });

    const { data: occupancyData, isLoading: isOccupancyLoading } = useHistoricalData({
        device_id: 'cctv_subsystem',
        datapoints: ['occupancy_current'],
        resampling: '5min',
        start_timestamp: dateRange.start_timestamp,
        end_timestamp: dateRange.end_timestamp
    });

    // Fetch data for AHU Status chart
    const { data: ahuData, isLoading: isAhuLoading } = useHistoricalData({
        device_id: 'ac_subsystem',
        datapoints: ['mode_cool_count', 'mode_fan_count'],
        resampling: '5min',
        start_timestamp: dateRange.start_timestamp,
        end_timestamp: dateRange.end_timestamp,
        resample_method: 'last'
    });

    // Fetch working percentage data (now used in AHU Status chart)
    const { data: workingData, isLoading: isWorkingLoading } = useHistoricalData({
        device_id: 'ac_subsystem',
        datapoints: ['avg_working_percentage'],
        resampling: '5min',
        start_timestamp: dateRange.start_timestamp,
        end_timestamp: dateRange.end_timestamp
    });

    // Process data for Power Consumption & Generation chart
    useEffect(() => {
        if (!isAcPowerLoading && !isSolarLoading && !isOutdoorTempLoading &&
            acPowerData && solarData && outdoorTempData) {
            try {
                console.log('Processing energy chart data');

                // Create a map to store data points by timestamp
                const dataMap = new Map<string, ChartDataPoint>();

                // Process AC power data
                // Handle both old and new response formats
                const acDataArray = Array.isArray(acPowerData) ? acPowerData :
                    (acPowerData.data && Array.isArray(acPowerData.data)) ? acPowerData.data :
                        (acPowerData.data && acPowerData.data.data) ? acPowerData.data.data : [];

                if (acDataArray.length > 0 && acDataArray[0].values) {
                    acDataArray[0].values.forEach((point: ApiDataPoint) => {
                        const time = new Date(point.timestamp).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false
                        });

                        dataMap.set(time, {
                            time,
                            acPower: point.value
                        });
                    });
                }

                // Process solar data
                const solarDataArray = Array.isArray(solarData) ? solarData :
                    (solarData.data && Array.isArray(solarData.data)) ? solarData.data :
                        (solarData.data && solarData.data.data) ? solarData.data.data : [];

                if (solarDataArray.length > 0) {
                    // Process solar power data
                    const solarPowerData = solarDataArray.find(item => item.datapoint === 'power');
                    if (solarPowerData && solarPowerData.values) {
                        solarPowerData.values.forEach((point: ApiDataPoint) => {
                            const time = new Date(point.timestamp).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: false
                            });

                            const existingPoint = dataMap.get(time) || { time };
                            existingPoint.solarGeneration = point.value;
                            // Calculate total power if grid import is available
                            if (existingPoint.gridImport !== undefined && existingPoint.solarGeneration !== undefined) {
                                existingPoint.totalPower = existingPoint.solarGeneration + existingPoint.gridImport;
                            }
                            dataMap.set(time, existingPoint);
                        });
                    }

                    // Process expected solar power data
                    const expectedSolarData = solarDataArray.find(item => item.datapoint === 'expected_power');
                    if (expectedSolarData && expectedSolarData.values) {
                        expectedSolarData.values.forEach((point: ApiDataPoint) => {
                            const time = new Date(point.timestamp).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: false
                            });

                            const existingPoint = dataMap.get(time) || { time };
                            existingPoint.expectedSolar = point.value;
                            dataMap.set(time, existingPoint);
                        });
                    }

                    // Process grid power data
                    const gridPowerData = solarDataArray.find(item => item.datapoint === 'grid_power');
                    if (gridPowerData && gridPowerData.values) {
                        gridPowerData.values.forEach((point: ApiDataPoint) => {
                            const time = new Date(point.timestamp).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: false
                            });

                            const existingPoint = dataMap.get(time) || { time };
                            existingPoint.gridImport = point.value;
                            // Calculate total power = solar generation + grid import
                            if (existingPoint.solarGeneration !== undefined && existingPoint.gridImport !== undefined) {
                                existingPoint.totalPower = existingPoint.solarGeneration + existingPoint.gridImport;
                            }
                            dataMap.set(time, existingPoint);
                        });
                    }
                }

                // Process outdoor temperature data
                const tempDataArray = Array.isArray(outdoorTempData) ? outdoorTempData :
                    (outdoorTempData.data && Array.isArray(outdoorTempData.data)) ? outdoorTempData.data :
                        (outdoorTempData.data && outdoorTempData.data.data) ? outdoorTempData.data.data : [];

                if (tempDataArray.length > 0 && tempDataArray[0].values) {
                    tempDataArray[0].values.forEach((point: ApiDataPoint) => {
                        const time = new Date(point.timestamp).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false
                        });

                        const existingPoint = dataMap.get(time) || { time };
                        existingPoint.outdoorTemp = point.value;
                        dataMap.set(time, existingPoint);
                    });
                }

                // Convert map to array and sort by time
                const chartData = Array.from(dataMap.values()).sort((a, b) => {
                    return a.time.localeCompare(b.time);
                });

                // Generate full day time points if needed (for x-axis consistency)
                const fullDayData = generateFullDayTimePoints(chartData);

                console.log('Energy chart data processed:', fullDayData);
                setEnergyChartData(fullDayData);
            } catch (error) {
                console.error('Error processing energy chart data:', error);
                setEnergyChartData([]);
            }
        }
    }, [acPowerData, solarData, outdoorTempData, isAcPowerLoading, isSolarLoading, isOutdoorTempLoading, dateRange.start_timestamp, dateRange.end_timestamp]);

    // Process data for Temperature Control & Occupancy chart
    useEffect(() => {
        if (!isZoneLoading && !isOccupancyLoading && zoneData && occupancyData) {
            try {
                console.log('Processing temperature chart data');

                // Create a map to store data points by timestamp
                const dataMap = new Map<string, ChartDataPoint>();

                // Process zone data (temperature setpoint and room temperature)
                const zoneDataArray = Array.isArray(zoneData) ? zoneData :
                    (zoneData.data && Array.isArray(zoneData.data)) ? zoneData.data :
                        (zoneData.data && zoneData.data.data) ? zoneData.data.data : [];

                if (zoneDataArray.length > 0) {
                    // Process temperature setpoint data
                    const setTempData = zoneDataArray.find(item => item.datapoint === 'avg_temperature_setpoint');
                    if (setTempData && setTempData.values) {
                        setTempData.values.forEach((point: ApiDataPoint) => {
                            const time = new Date(point.timestamp).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: false
                            });

                            dataMap.set(time, {
                                time,
                                setTemp: point.value
                            });
                        });
                    }

                    // Process room temperature data
                    const roomTempData = zoneDataArray.find(item => item.datapoint === 'avg_room_temperature');
                    if (roomTempData && roomTempData.values) {
                        roomTempData.values.forEach((point: ApiDataPoint) => {
                            const time = new Date(point.timestamp).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: false
                            });

                            const existingPoint = dataMap.get(time) || { time };
                            existingPoint.avgIndoorTemp = point.value;
                            dataMap.set(time, existingPoint);
                        });
                    }
                }

                // Process occupancy data
                const occupancyDataArray = Array.isArray(occupancyData) ? occupancyData :
                    (occupancyData.data && Array.isArray(occupancyData.data)) ? occupancyData.data :
                        (occupancyData.data && occupancyData.data.data) ? occupancyData.data.data : [];

                if (occupancyDataArray.length > 0 && occupancyDataArray[0].values) {
                    occupancyDataArray[0].values.forEach((point: ApiDataPoint) => {
                        const time = new Date(point.timestamp).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false
                        });

                        const existingPoint = dataMap.get(time) || { time };
                        existingPoint.occupancy = point.value;
                        dataMap.set(time, existingPoint);
                    });
                }

                // Convert map to array and sort by time
                const chartData = Array.from(dataMap.values()).sort((a, b) => {
                    return a.time.localeCompare(b.time);
                });

                // Generate full day time points if needed (for x-axis consistency)
                const fullDayData = generateFullDayTimePoints(chartData);

                console.log('Temperature chart data processed:', fullDayData);
                setTemperatureChartData(fullDayData);
            } catch (error) {
                console.error('Error processing temperature chart data:', error);
                setTemperatureChartData([]);
            }
        }
    }, [zoneData, occupancyData, isZoneLoading, isOccupancyLoading, dateRange.start_timestamp, dateRange.end_timestamp]);

    // Process data for AHU Status chart
    useEffect(() => {
        if (!isAhuLoading && !isWorkingLoading && ahuData && workingData) {
            try {
                console.log('Processing AHU chart data');

                // Create a map to store data points by timestamp
                const dataMap = new Map<string, ChartDataPoint>();

                // Process AHU data (mode counts)
                const ahuDataArray = Array.isArray(ahuData) ? ahuData :
                    (ahuData.data && Array.isArray(ahuData.data)) ? ahuData.data :
                        (ahuData.data && ahuData.data.data) ? ahuData.data.data : [];

                if (ahuDataArray.length > 0) {
                    // Process cooling mode count data
                    const coolModeData = ahuDataArray.find(item => item.datapoint === 'mode_cool_count');
                    if (coolModeData && coolModeData.values) {
                        coolModeData.values.forEach((point: ApiDataPoint) => {
                            const time = new Date(point.timestamp).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: false
                            });

                            dataMap.set(time, {
                                time,
                                coolingMode: point.value
                            });
                        });
                    }

                    // Process fan mode count data
                    const fanModeData = ahuDataArray.find(item => item.datapoint === 'mode_fan_count');
                    if (fanModeData && fanModeData.values) {
                        fanModeData.values.forEach((point: ApiDataPoint) => {
                            const time = new Date(point.timestamp).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: false
                            });

                            const existingPoint = dataMap.get(time) || { time };
                            existingPoint.fanMode = point.value;
                            dataMap.set(time, existingPoint);
                        });
                    }
                }

                // Process working percentage data
                const workingDataArray = Array.isArray(workingData) ? workingData :
                    (workingData.data && Array.isArray(workingData.data)) ? workingData.data :
                        (workingData.data && workingData.data.data) ? workingData.data.data : [];

                if (workingDataArray.length > 0 && workingDataArray[0].values) {
                    workingDataArray[0].values.forEach((point: ApiDataPoint) => {
                        const time = new Date(point.timestamp).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false
                        });

                        const existingPoint = dataMap.get(time) || { time };
                        existingPoint.totalWorking = point.value;
                        dataMap.set(time, existingPoint);
                    });
                }

                // Convert map to array and sort by time
                const chartData = Array.from(dataMap.values()).sort((a, b) => {
                    return a.time.localeCompare(b.time);
                });

                // Generate full day time points if needed (for x-axis consistency)
                const fullDayData = generateFullDayTimePoints(chartData);

                console.log('AHU chart data processed:', fullDayData);
                setAhuChartData(fullDayData);
            } catch (error) {
                console.error('Error processing AHU chart data:', error);
                setAhuChartData([]);
            }
        }
    }, [ahuData, workingData, isAhuLoading, isWorkingLoading, dateRange.start_timestamp, dateRange.end_timestamp]);

    // X-axis label formatter to show only specific times
    const formatXAxisLabel = (tickItem: string) => {
        return tickItem === '06:00' || tickItem === '12:00' || tickItem === '18:00' ? tickItem : '';
    };

    // X-axis tick filter
    const getXAxisTicks = (data: ChartDataPoint[]) => {
        return data.map(item => item.time).filter(time =>
            time === '06:00' || time === '12:00' || time === '18:00'
        );
    };

    // Energy Chart Component
    const EnergyChart = useMemo(() => {
        return (
            <div className="w-full h-full relative">
                <div className="absolute -top-2 left-0 right-0 z-10 flex justify-center">
                    <div className="flex flex-wrap gap-x-3 gap-y-0 text-[10px] text-gray-600 bg-white/90 px-1 py-0.5 rounded">
                        <div 
                            className={`flex items-center gap-1 cursor-pointer transition-opacity ${!energyLegendVisible.acPower ? 'opacity-50' : ''}`}
                            onClick={() => setEnergyLegendVisible(prev => ({ ...prev, acPower: !prev.acPower }))}
                        >
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: THEME.consumption.line }}></div>
                            <span>A/C Power</span>
                        </div>
                        <div 
                            className={`flex items-center gap-1 cursor-pointer transition-opacity ${!energyLegendVisible.totalPower ? 'opacity-50' : ''}`}
                            onClick={() => setEnergyLegendVisible(prev => ({ ...prev, totalPower: !prev.totalPower }))}
                        >
                            <div className="w-2 h-2 rounded-full bg-black"></div>
                            <span>Total Power</span>
                        </div>
                        <div 
                            className={`flex items-center gap-1 cursor-pointer transition-opacity ${!energyLegendVisible.gridImport ? 'opacity-50' : ''}`}
                            onClick={() => setEnergyLegendVisible(prev => ({ ...prev, gridImport: !prev.gridImport }))}
                        >
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#7D3BBF' }}></div>
                            <span>Grid Import</span>
                        </div>
                        <div 
                            className={`flex items-center gap-1 cursor-pointer transition-opacity ${!energyLegendVisible.solarGeneration ? 'opacity-50' : ''}`}
                            onClick={() => setEnergyLegendVisible(prev => ({ ...prev, solarGeneration: !prev.solarGeneration }))}
                        >
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: THEME.solar.actual }}></div>
                            <span>Solar Power</span>
                        </div>
                        <div 
                            className={`flex items-center gap-1 cursor-pointer transition-opacity ${!energyLegendVisible.expectedSolar ? 'opacity-50' : ''}`}
                            onClick={() => setEnergyLegendVisible(prev => ({ ...prev, expectedSolar: !prev.expectedSolar }))}
                        >
                            <div className="w-2 h-1 rounded-full" style={{ backgroundColor: THEME.solar.expected, backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 2px, white 2px, white 4px)' }}></div>
                            <span>Expected Solar</span>
                        </div>
                        <div 
                            className={`flex items-center gap-1 cursor-pointer transition-opacity ${!energyLegendVisible.outdoorTemp ? 'opacity-50' : ''}`}
                            onClick={() => setEnergyLegendVisible(prev => ({ ...prev, outdoorTemp: !prev.outdoorTemp }))}
                        >
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: THEME.temperature.outdoor }}></div>
                            <span>Outdoor Temp</span>
                        </div>
                        <div 
                            className={`flex items-center gap-1 cursor-pointer transition-opacity ${!energyLegendVisible.avgIndoorTemp ? 'opacity-50' : ''}`}
                            onClick={() => setEnergyLegendVisible(prev => ({ ...prev, avgIndoorTemp: !prev.avgIndoorTemp }))}
                        >
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: THEME.temperature.indoor }}></div>
                            <span>Indoor Temp</span>
                        </div>
                    </div>
                </div>
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                        data={energyChartData}
                        margin={{ top: 15, right: 10, left: 10, bottom: 20 }}
                    >
                        <CartesianGrid strokeDasharray="2 2" stroke="#E0E0E0" horizontal={true} vertical={false} />
                        <XAxis
                            dataKey="time"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 10, fill: '#666' }}
                            ticks={getXAxisTicks(energyChartData)}
                            height={12}
                        />
                        <YAxis
                            yAxisId="power"
                            domain={[0, 1500]}
                            ticks={[0, 500, 1000, 1500]}
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 9, fill: '#666' }}
                            width={18}
                        />
                        <YAxis
                            yAxisId="temp"
                            orientation="right"
                            domain={[15, 50]}
                            ticks={[15, 20, 25, 30, 35, 40, 45, 50]}
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 9, fill: '#666' }}
                            width={15}
                        />
                        <text x="2%" y="95%" fontSize={10} fill="#666" textAnchor="start">
                            Power (kW)
                        </text>
                        <text x="98%" y="95%" fontSize={10} fill="#666" textAnchor="end">
                            Temperature (°C)
                        </text>
                        <Tooltip
                            formatter={formatTooltip}
                            labelFormatter={formatLabel}
                            contentStyle={{
                                fontSize: '10px',
                                fontFamily: 'Inter',
                                padding: '4px',
                                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                border: '1px solid #ccc',
                                borderRadius: '3px'
                            }}
                        />

                        {energyLegendVisible.acPower && (
                            <Area
                                yAxisId="power"
                                type="linear"
                                dataKey="acPower"
                                stroke={THEME.consumption.line}
                                fill={THEME.consumption.area}
                                strokeWidth={1.5}
                                dot={false}
                                name="A/C Power"
                            />
                        )}
                        {energyLegendVisible.totalPower && (
                            <Area
                                yAxisId="power"
                                type="linear"
                                dataKey="totalPower"
                                stroke="#000000"
                                fill="rgba(0, 0, 0, 0.1)"
                                strokeWidth={1.5}
                                dot={false}
                                name="Total Power"
                            />
                        )}
                        {energyLegendVisible.gridImport && (
                            <Area
                                yAxisId="power"
                                type="linear"
                                dataKey="gridImport"
                                stroke="#7D3BBF"
                                fill="rgba(125, 59, 191, 0.1)"
                                strokeWidth={1.5}
                                dot={false}
                                name="Grid Import"
                            />
                        )}
                        {energyLegendVisible.solarGeneration && (
                            <Area
                                yAxisId="power"
                                type="linear"
                                dataKey="solarGeneration"
                                stroke={THEME.solar.actual}
                                fill="rgba(242, 170, 50, 0.1)"
                                strokeWidth={1.5}
                                dot={false}
                                name="Solar Power"
                            />
                        )}
                        {energyLegendVisible.expectedSolar && (
                            <Area
                                yAxisId="power"
                                type="linear"
                                dataKey="expectedSolar"
                                stroke={THEME.solar.expected}
                                fill="none"
                                strokeWidth={1.5}
                                strokeDasharray="5 5"
                                dot={false}
                                name="Expected Solar Power"
                            />
                        )}
                        {energyLegendVisible.outdoorTemp && (
                            <Area
                                yAxisId="temp"
                                type="linear"
                                dataKey="outdoorTemp"
                                stroke={THEME.temperature.outdoor}
                                fill="none"
                                strokeWidth={1.5}
                                dot={false}
                                name="Outdoor Temp"
                            />
                        )}
                        {energyLegendVisible.avgIndoorTemp && (
                            <Area
                                yAxisId="temp"
                                type="linear"
                                dataKey="avgIndoorTemp"
                                stroke={THEME.temperature.indoor}
                                fill="none"
                                strokeWidth={1.5}
                                dot={false}
                                name="Indoor Temp"
                            />
                        )}
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        );
    }, [energyChartData, energyLegendVisible]);

    // Temperature Chart Component
    const TemperatureChart = useMemo(() => {
        const occupancyValues = temperatureChartData.map(item => item.occupancy).filter(Boolean) as number[];
        const maxOccupancy = Math.max(...occupancyValues, 1);
        const occupancyMax = Math.ceil(maxOccupancy * 1.2 / 100) * 100;

        return (
            <div className="w-full h-full relative">
                <div className="absolute -top-2 left-0 right-0 z-10 flex justify-center">
                    <div className="flex flex-wrap gap-x-3 gap-y-0 text-[10px] text-gray-600 bg-white/90 px-1 py-0.5 rounded">
                        <div 
                            className={`flex items-center gap-1 cursor-pointer transition-opacity ${!temperatureLegendVisible.occupancy ? 'opacity-50' : ''}`}
                            onClick={() => setTemperatureLegendVisible(prev => ({ ...prev, occupancy: !prev.occupancy }))}
                        >
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: THEME.occupancy }}></div>
                            <span>Occupancy</span>
                        </div>
                        <div 
                            className={`flex items-center gap-1 cursor-pointer transition-opacity ${!temperatureLegendVisible.setTemp ? 'opacity-50' : ''}`}
                            onClick={() => setTemperatureLegendVisible(prev => ({ ...prev, setTemp: !prev.setTemp }))}
                        >
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: THEME.temperature.setTemp }}></div>
                            <span>Setpoint Temp</span>
                        </div>
                        <div 
                            className={`flex items-center gap-1 cursor-pointer transition-opacity ${!temperatureLegendVisible.avgIndoorTemp ? 'opacity-50' : ''}`}
                            onClick={() => setTemperatureLegendVisible(prev => ({ ...prev, avgIndoorTemp: !prev.avgIndoorTemp }))}
                        >
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: THEME.temperature.indoor }}></div>
                            <span>Indoor Temp</span>
                        </div>
                    </div>
                </div>
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                        data={temperatureChartData}
                        margin={{ top: 15, right: 10, left: 10, bottom: 20 }}
                    >
                        <CartesianGrid strokeDasharray="2 2" stroke="#E0E0E0" horizontal={true} vertical={false} />
                        <XAxis
                            dataKey="time"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 10, fill: '#666' }}
                            ticks={getXAxisTicks(temperatureChartData)}
                            height={12}
                        />
                        <YAxis
                            yAxisId="occupancy"
                            domain={[0, occupancyMax]}
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 9, fill: '#666' }}
                            width={18}
                        />
                        <YAxis
                            yAxisId="temp"
                            orientation="right"
                            domain={[15, 50]}
                            ticks={[15, 20, 25, 30, 35, 40, 45, 50]}
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 9, fill: '#666' }}
                            width={15}
                        />
                        <text x="2%" y="95%" fontSize={10} fill="#666" textAnchor="start">
                            Occupancy (persons)
                        </text>
                        <text x="98%" y="95%" fontSize={10} fill="#666" textAnchor="end">
                            Temperature (°C)
                        </text>
                        <Tooltip
                            formatter={formatTooltip}
                            labelFormatter={formatLabel}
                            contentStyle={{
                                fontSize: '8px',
                                fontFamily: 'Inter',
                                padding: '3px',
                                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                border: '1px solid #ccc',
                                borderRadius: '2px'
                            }}
                        />

                        {temperatureLegendVisible.occupancy && (
                            <Line
                                yAxisId="occupancy"
                                type="linear"
                                dataKey="occupancy"
                                stroke={THEME.occupancy}
                                strokeWidth={1.5}
                                dot={false}
                                name="Occupancy"
                            />
                        )}
                        {temperatureLegendVisible.setTemp && (
                            <Line
                                yAxisId="temp"
                                type="linear"
                                dataKey="setTemp"
                                stroke={THEME.temperature.setTemp}
                                strokeWidth={1.5}
                                dot={false}
                                name="Setpoint Temp"
                            />
                        )}
                        {temperatureLegendVisible.avgIndoorTemp && (
                            <Line
                                yAxisId="temp"
                                type="linear"
                                dataKey="avgIndoorTemp"
                                stroke={THEME.temperature.indoor}
                                strokeWidth={1.5}
                                dot={false}
                                name="Indoor Temp"
                            />
                        )}
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        );
    }, [temperatureChartData, temperatureLegendVisible]);

    // AHU Chart Component with custom tooltip
    const AhuChart = useMemo(() => {
        const CustomTooltip = ({ active, payload, label }: any) => {
            if (active && payload && payload.length) {
                let totalUnits = 0;
                payload.forEach((entry: any) => {
                    if (entry.dataKey !== 'totalWorking') {
                        totalUnits += entry.value || 0;
                    }
                });

                return (
                    <div style={{
                        fontSize: '10px',
                        fontFamily: 'Inter',
                        padding: '4px',
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        border: '1px solid #ccc',
                        borderRadius: '3px'
                    }}>
                        <p style={{ margin: '2px 0' }}>{label}</p>
                        <p style={{ margin: '2px 0' }}>Total Units: {totalUnits.toFixed(0)}</p>
                        {payload.map((entry: any, index: number) => (
                            <div key={index} style={{ display: 'flex', alignItems: 'center', margin: '2px 0' }}>
                                <span style={{
                                    display: 'inline-block',
                                    width: '8px',
                                    height: '8px',
                                    backgroundColor: entry.color,
                                    marginRight: '4px',
                                    borderRadius: '50%'
                                }}></span>
                                <span style={{ fontSize: '9px' }}>
                                    {entry.name}: {entry.dataKey === 'totalWorking'
                                        ? `${entry.value?.toFixed(1)}%`
                                        : entry.value?.toFixed(0)}
                                </span>
                            </div>
                        ))}
                    </div>
                );
            }
            return null;
        };

        return (
            <div className="w-full h-full relative">
                <div className="absolute -top-2 left-0 right-0 z-10 flex justify-center">
                    <div className="flex flex-wrap gap-x-3 gap-y-0 text-[10px] text-gray-600 bg-white/90 px-1 py-0.5 rounded">
                        <div 
                            className={`flex items-center gap-1 cursor-pointer transition-opacity ${!ahuLegendVisible.coolingMode ? 'opacity-50' : ''}`}
                            onClick={() => setAhuLegendVisible(prev => ({ ...prev, coolingMode: !prev.coolingMode }))}
                        >
                            <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: THEME.ahu.cooling }}></div>
                            <span>Cool Modes</span>
                        </div>
                        <div 
                            className={`flex items-center gap-1 cursor-pointer transition-opacity ${!ahuLegendVisible.fanMode ? 'opacity-50' : ''}`}
                            onClick={() => setAhuLegendVisible(prev => ({ ...prev, fanMode: !prev.fanMode }))}
                        >
                            <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: THEME.ahu.fan }}></div>
                            <span>Fan Modes</span>
                        </div>
                        <div 
                            className={`flex items-center gap-1 cursor-pointer transition-opacity ${!ahuLegendVisible.totalWorking ? 'opacity-50' : ''}`}
                            onClick={() => setAhuLegendVisible(prev => ({ ...prev, totalWorking: !prev.totalWorking }))}
                        >
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: THEME.working }}></div>
                            <span>Working %</span>
                        </div>
                    </div>
                </div>
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                        data={ahuChartData}
                        margin={{ top: 15, right: 10, left: 10, bottom: 20 }}
                        barCategoryGap={0}
                    >
                        <CartesianGrid strokeDasharray="2 2" stroke="#E0E0E0" horizontal={true} vertical={false} />
                        <XAxis
                            dataKey="time"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 10, fill: '#666' }}
                            ticks={getXAxisTicks(ahuChartData)}
                            height={12}
                        />
                        <YAxis
                            yAxisId="units"
                            domain={[0, 10]}
                            ticks={[0, 2, 4, 6, 8, 10]}
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 9, fill: '#666' }}
                            width={18}
                        />
                        <YAxis
                            yAxisId="working"
                            orientation="right"
                            domain={[0, 160]}
                            ticks={[0, 160]}
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 9, fill: '#666' }}
                            tickFormatter={(value) => `${value}%`}
                            width={20}
                        />
                        <text x="2%" y="95%" fontSize={10} fill="#666" textAnchor="start">
                            Number of Units
                        </text>
                        <text x="98%" y="95%" fontSize={10} fill="#666" textAnchor="end">
                            Working %
                        </text>
                        <Tooltip content={<CustomTooltip />} />

                        {ahuLegendVisible.coolingMode && (
                            <Bar
                                yAxisId="units"
                                dataKey="coolingMode"
                                stackId="total"
                                fill={THEME.ahu.cooling}
                                name="Cool Modes"
                                stroke="none"
                            />
                        )}
                        {ahuLegendVisible.fanMode && (
                            <Bar
                                yAxisId="units"
                                dataKey="fanMode"
                                stackId="total"
                                fill={THEME.ahu.fan}
                                name="Fan Modes"
                                stroke="none"
                            />
                        )}
                        {ahuLegendVisible.totalWorking && (
                            <Line
                                yAxisId="working"
                                type="linear"
                                dataKey="totalWorking"
                                stroke={THEME.working}
                                strokeWidth={1.5}
                                dot={false}
                                name="Working %"
                            />
                        )}
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        );
    }, [ahuChartData, ahuLegendVisible]);

    return (
        <div className="w-full flex flex-col gap-2">
            {/* Event Timeline */}
            {eventTypes && (
                <EventTimeline
                    eventTypes={eventTypes}
                    profileName={profileName}
                    showEventTypes={true}
                    eventLogData={eventLogData}
                    isLoading={eventLogLoading}
                    selectedDate={selectedDate}
                />
            )}

            {/* Energy Chart */}
            <div className="flex flex-col">
                <div className="h-[200px]">
                    {isAcPowerLoading || isSolarLoading || isOutdoorTempLoading || isSitesLoading ? (
                        <div className="flex items-center justify-center h-full">
                            <div className="flex gap-1 items-end">
                                <div className="w-1 h-3 bg-primary rounded-sm animate-wave-loader"></div>
                                <div className="w-1 h-3 bg-primary rounded-sm animate-wave-loader"></div>
                                <div className="w-1 h-3 bg-primary rounded-sm animate-wave-loader"></div>
                            </div>
                        </div>
                    ) : energyChartData.length > 0 ? (
                        EnergyChart
                    ) : (
                        <div className="flex items-center justify-center h-full text-[10px] text-gray-400">
                            No data available
                        </div>
                    )}
                </div>
            </div>

            {/* Temperature Chart */}
            <div className="flex flex-col">
                <div className="h-[200px]">
                    {isZoneLoading || isWorkingLoading || isOccupancyLoading || isSitesLoading ? (
                        <div className="flex items-center justify-center h-full">
                            <div className="flex gap-1 items-end">
                                <div className="w-1 h-3 bg-primary rounded-sm animate-wave-loader"></div>
                                <div className="w-1 h-3 bg-primary rounded-sm animate-wave-loader"></div>
                                <div className="w-1 h-3 bg-primary rounded-sm animate-wave-loader"></div>
                            </div>
                        </div>
                    ) : temperatureChartData.length > 0 ? (
                        TemperatureChart
                    ) : (
                        <div className="flex items-center justify-center h-full text-[10px] text-gray-400">
                            No data available
                        </div>
                    )}
                </div>
            </div>

            {/* AHU Chart */}
            <div className="flex flex-col">
                <div className="h-[200px]">
                    {isAhuLoading || isSitesLoading ? (
                        <div className="flex items-center justify-center h-full">
                            <div className="flex gap-1 items-end">
                                <div className="w-1 h-3 bg-primary rounded-sm animate-wave-loader"></div>
                                <div className="w-1 h-3 bg-primary rounded-sm animate-wave-loader"></div>
                                <div className="w-1 h-3 bg-primary rounded-sm animate-wave-loader"></div>
                            </div>
                        </div>
                    ) : ahuChartData.length > 0 ? (
                        AhuChart
                    ) : (
                        <div className="flex items-center justify-center h-full text-[10px] text-gray-400">
                            No data available
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Dashboard;